import { useCallback } from 'react';
import { toast } from 'sonner';
import { useStarData } from './useStarData';
import { WishItem, getCurrentTimestamp } from '@/utils/storage';
import { celebrateAchievement } from '@/utils/animations';

export const useWishListManager = () => {
  const { data, updateData } = useStarData();

  const addWishItem = useCallback(async (itemData: Omit<WishItem, 'id' | 'dateAdded'>) => {
    if (!data) return;

    const newItem: WishItem = {
      ...itemData,
      id: `wish_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      dateAdded: getCurrentTimestamp()
    };

    const updatedData = {
      ...data,
      wishList: [...(data.wishList || []), newItem]
    };

    try {
      await updateData(updatedData);
      toast.success(`"${newItem.title}" added to your wish list!`);
      return newItem;
    } catch (error) {
      console.error('Error adding wish list item:', error);
      toast.error('Failed to add item to wish list');
      throw error;
    }
  }, [data, updateData]);

  const updateWishItem = useCallback(async (itemId: string, updates: Partial<WishItem>) => {
    if (!data?.wishList) return;

    const itemIndex = data.wishList.findIndex(item => item.id === itemId);
    if (itemIndex === -1) {
      toast.error('Wish list item not found');
      return;
    }

    const currentItem = data.wishList[itemIndex];
    const updatedItem = { ...currentItem, ...updates };

    // Clamp progress to 0-100 so out-of-range values can't slip through
    if (updatedItem.progress !== undefined) {
      updatedItem.progress = Math.max(0, Math.min(100, updatedItem.progress));
    }

    const updatedWishList = [...data.wishList];
    updatedWishList[itemIndex] = updatedItem;

    const updatedData = {
      ...data,
      wishList: updatedWishList
    };

    try {
      await updateData(updatedData);
      
      // If marking as achieved for the first time, show celebration
      if (updates.achieved && !currentItem.achieved) {
        celebrateAchievement(`🎉 You achieved: ${updatedItem.title}!`);
        toast.success(`Congratulations! You achieved "${updatedItem.title}"!`);
      } else {
        toast.success('Wish list item updated!');
      }
      
      return updatedItem;
    } catch (error) {
      console.error('Error updating wish list item:', error);
      toast.error('Failed to update wish list item');
      throw error;
    }
  }, [data, updateData]);

  const deleteWishItem = useCallback(async (itemId: string) => {
    if (!data?.wishList) return;

    const item = data.wishList.find(item => item.id === itemId);
    if (!item) {
      toast.error('Wish list item not found');
      return;
    }

    const updatedWishList = data.wishList.filter(item => item.id !== itemId);

    const updatedData = {
      ...data,
      wishList: updatedWishList
    };

    try {
      await updateData(updatedData);
      toast.success(`"${item.title}" removed from wish list`);
    } catch (error) {
      console.error('Error deleting wish list item:', error);
      toast.error('Failed to delete wish list item');
      throw error;
    }
  }, [data, updateData]);

  const markWishItemAchieved = useCallback(async (itemId: string) => {
    const updates: Partial<WishItem> = {
      achieved: true,
      achievedDate: getCurrentTimestamp(),
      progress: 100
    };
    
    return updateWishItem(itemId, updates);
  }, [updateWishItem]);

  const updateWishItemProgress = useCallback(async (itemId: string, progress: number) => {
    const clampedProgress = Math.max(0, Math.min(100, progress));
    const updates: Partial<WishItem> = {
      progress: clampedProgress
    };
    
    return updateWishItem(itemId, updates);
  }, [updateWishItem]);

  const toggleWishListEnabled = useCallback(async (enabled: boolean) => {
    if (!data) return;

    const updatedTabSettings = {
      ...data.tabSettings,
      wishListEnabled: enabled
    };

    const updatedData = {
      ...data,
      tabSettings: updatedTabSettings
    };

    try {
      await updateData(updatedData);
      toast.success(`Wish list ${enabled ? 'enabled' : 'disabled'}`);
      return updatedData;
    } catch (error) {
      console.error('Error toggling wish list:', error);
      toast.error('Failed to update wish list setting');
      throw error;
    }
  }, [data, updateData]);

  // Helper function to calculate affordability
  const calculateAffordability = useCallback((cost: number) => {
    if (!data) return 0;
    
    if (data.walletSettings?.enabled && data.walletData) {
      return (data.walletData.currentBalance / cost) * 100;
    } else {
      return (data.stars / cost) * 100;
    }
  }, [data]);

  // Helper function to get current balance/currency info
  const getCurrencyInfo = useCallback(() => {
    if (!data) return { currency: 'stars', balance: 0 };
    
    if (data.walletSettings?.enabled && data.walletData) {
      return {
        currency: '$',
        balance: data.walletData.currentBalance
      };
    } else {
      return {
        currency: 'stars',
        balance: data.stars
      };
    }
  }, [data]);

  // Stats and analytics
  const getWishListStats = useCallback(() => {
    if (!data?.wishList) {
      return {
        total: 0,
        achieved: 0,
        unachieved: 0,
        totalCost: 0,
        achievedCost: 0,
        averageProgress: 0,
        nearlyAffordable: 0 // Items that are 80%+ affordable
      };
    }

    const { currency, balance } = getCurrencyInfo();
    const nearlyAffordableThreshold = 0.8;

    const stats = data.wishList.reduce(
      (acc, item) => {
        acc.total++;
        acc.totalCost += item.cost;
        
        if (item.achieved) {
          acc.achieved++;
          acc.achievedCost += item.cost;
        } else {
          acc.unachieved++;
          const affordability = calculateAffordability(item.cost);
          if (affordability >= nearlyAffordableThreshold * 100) {
            acc.nearlyAffordable++;
          }
        }
        
        acc.averageProgress += item.progress;
        return acc;
      },
      {
        total: 0,
        achieved: 0,
        unachieved: 0,
        totalCost: 0,
        achievedCost: 0,
        averageProgress: 0,
        nearlyAffordable: 0
      }
    );

    stats.averageProgress = stats.total > 0 ? stats.averageProgress / stats.total : 0;

    return {
      ...stats,
      currency,
      currentBalance: balance
    };
  }, [data, calculateAffordability, getCurrencyInfo]);

  return {
    // Data
    wishList: data?.wishList || [],
    wishListEnabled: data?.tabSettings?.wishListEnabled || false,
    
    // Actions
    addWishItem,
    updateWishItem,
    deleteWishItem,
    markWishItemAchieved,
    updateWishItemProgress,
    toggleWishListEnabled,
    
    // Helpers
    calculateAffordability,
    getCurrencyInfo,
    getWishListStats
  };
};